import React, { useState, useEffect, useMemo } from "react";
import { v4 as uuidv4 } from "uuid";
import { useOutletContext } from "react-router-dom";
import { isFunction } from "lodash";

// Hook
import useAuthScope from "hooks/useAuthScope";
import useDataSourceManager from "hooks/useDataSourceManager";

// Component
import Loading from "../loading";
import MemberUser from "./memberUser";
import DeleteUserModal from "./deleteModal";

const OrgUserManagement = ({ entId, orgId, userId, closeDrawerCallback }) => {
  const stored = localStorage.getItem("selectBreadcrumbs");
  const { dataMap, getData, sendRequest } = useDataSourceManager();

  // State
  const [isLoading, setIsLoading] = useState(true);
  const [listOrgUsersUuid, setListOrgUsersUuid] = useState(uuidv4());
  const { scopeData, scopeReady } = useOutletContext();
  const { isAuthReady } = useAuthScope();
  const [currentUserOrgRoleName, setCurrentUserOrgRoleName] = useState(null);
  const [currentUserOrgRoleId, setCurrentUserOrgRoleId] = useState(null);
  const [tempDirectAccess, setTempDirectAccess] = useState(null);
  const [tempMspAccess, setTempMspAccess] = useState(null);
  const [userDirectAccessData, setUserDirectAccessData] = useState([]);
  const [combinedUserData, setCombinedUserData] = useState([]);
  const [originalRowData, setOriginalRowData] = useState(null);
  const [selectedRowData, setSelectedRowData] = useState(null);
  const [openDeleteAccountModal, setOpenDeleteAccountModal] = useState(false);

  const breadcrumbs = JSON.parse(localStorage.getItem("selectBreadcrumbs"));
  const hasManageOrg = breadcrumbs.realm?.hasManageOrg === true;
  const allSitesName = useMemo(() => {
    const sites = (scopeData.site ?? []).map((site) => ({
      siteId: site.id,
      siteName: site.name,
      siteRoleId: site.roleId,
      siteRoleName: site.roleName,
    }));

    return sites.sort((a, b) => a.siteName.localeCompare(b.siteName));
  }, [scopeData.site]);
  const isViewer = currentUserOrgRoleName === "Viewer" || currentUserOrgRoleName === "None";

  // Method
  const getListOrgUsers = () => {
    // Those APIs don't implement yet.
    // /orgs/{orgId}/users/{userId}
    // /ents/{entId}/orgs/{orgId}/users/{userId}

    if (hasManageOrg && orgId) {
      sendRequest("/ents/:entId/orgs/:orgId/users.read", listOrgUsersUuid, { entId, orgId });
    } else {
      sendRequest("/orgs/:orgId/users.read", listOrgUsersUuid, { orgId });
    }
  };

  const getInitials = (name) => {
    if (!name || typeof name !== "string") return "?";
    return name
      .split(" ")
      .map((word) => word[0])
      .join("")
      .toUpperCase();
  };

  const formatStatus = (status) => {
    switch (status?.toLowerCase()) {
      case "active":
        return "Active";
      case "inactive":
        return "Inactive";
      case "expired":
        return "Expired";
      case "pending":
        return "Pending";
      default:
        return status;
    }
  };

  const handleCloseDeleteAccountModal = () => {
    setOpenDeleteAccountModal(false);
  };

  // Side Effect
  useEffect(() => {
    if (stored) {
      const parsed = JSON.parse(stored);
      const hasManageOrg = parsed?.realm?.hasManageOrg;

      if (hasManageOrg) {
        setCurrentUserOrgRoleName(parsed?.orgs?.roleName ?? null);
        setCurrentUserOrgRoleId(parsed?.orgs?.roleId ?? null);
      } else {
        setCurrentUserOrgRoleName(parsed?.realm?.roleName ?? null);
        setCurrentUserOrgRoleId(parsed?.realm?.roleId ?? null);
      }
    }
  }, []);

  useEffect(() => {
    if (isAuthReady) {
      setIsLoading(true);
      getListOrgUsers();
    }
  }, [isAuthReady])

  useEffect(() => {
    const orgUsersResponse = getData(listOrgUsersUuid);

    if (orgUsersResponse?.data) {
      setTempDirectAccess(orgUsersResponse.data.directAccesses || []);
      setTempMspAccess(orgUsersResponse.data.mspAccesses || []);
    }
  }, [dataMap]);

  useEffect(() => {
    const isAllLoaded = tempDirectAccess && tempMspAccess;

    if (!isAllLoaded) {
      return;
    }

    setUserDirectAccessData(tempDirectAccess);
  }, [tempDirectAccess, tempMspAccess]);

  useEffect(() => {
    const combined = [
      ...userDirectAccessData,
    ];

    const filtered = combined.filter(
      (user) => user.orgRoleName && user.orgRoleName !== "None"
    );

    setCombinedUserData(filtered);
  }, [userDirectAccessData]);

  useEffect(() => {
    if (combinedUserData.length > 0) {
      // Find the user by userId prop
      const user = combinedUserData.find((user) => user.id === userId);

      if (!user) {
        setOriginalRowData(null);
        setSelectedRowData(null);
        setIsLoading(false);
        isFunction(closeDrawerCallback) && closeDrawerCallback();
        return;
      }

      setOriginalRowData(user || null);
      setSelectedRowData(user || null);
      setIsLoading(false);
    }
  }, [combinedUserData]);

  if (isLoading) {
    return <Loading />;
  }

  return (
    <>
      <MemberUser
        entId={entId}
        orgId={orgId}
        isViewer={isViewer}
        currentUserOrgRoleId={currentUserOrgRoleId}
        setCurrentUserOrgRoleId={setCurrentUserOrgRoleId}
        allSitesName={allSitesName}
        originalRowData={originalRowData}
        setSelectedRowData={setSelectedRowData}
        selectedRowData={selectedRowData}
        setOpenDeleteAccountModal={setOpenDeleteAccountModal}
        setCurrentUserOrgRoleName={setCurrentUserOrgRoleName}
        getInitials={getInitials}
        formatStatus={formatStatus}
        closeDrawerCallback={closeDrawerCallback}
      />

      <DeleteUserModal
        openDeleteAccountModal={openDeleteAccountModal}
        handleCloseDeleteAccountModal={handleCloseDeleteAccountModal}
        selectedRowData={selectedRowData}
        entId={entId}
        orgId={orgId}
        closeDrawerCallback={closeDrawerCallback}
      />
    </>
  );
}

export default OrgUserManagement;